export const useUserStore = defineStore('user', {
  state: (): UserData => ({
    id: '',
    email: '',
    emailVerified: false,
    firstName: '',
    lastName: '',
    hasSso: false,
    isQualitiesUncertain: false,
    signatureQualities: {
      ses: { any: null },
      aes: { any: null },
      aesMinimal: { eidas: null, zertes: null },
      qes: { eidas: null, zertes: null },
      seal: {
        aes: {},
        qes: {},
      },
    },
    attributes: {},
    permissions: {
      visualSignatures: {
        claim: { canUpdate: false, defaultValue: false },
        date: { canUpdate: false, defaultValue: false },
        std: { canUpdate: false, defaultValue: false },
      },
    },
    groupInfo: {
      groupVersions: [],
      groups: [],
    },
  }),
  getters: {
    displayName(): string {
      if (this.firstName) return `${this.firstName} ${this.lastName}`.trimEnd()
      else return this.email
    },
    isBusinessMember(): boolean {
      const businessStore = useBusinessStore()
      return Boolean(this.id && businessStore.members.includes(this.id))
    },
    isBusinessAdmin(): boolean {
      const businessStore = useBusinessStore()
      return Boolean(this.id && businessStore.admins.includes(this.id))
    },
    isSsoSetupIncomplete(): boolean {
      const directSignStore = useDirectSignStore()
      return !directSignStore.isDirectSign && !this.firstName && !this.lastName && !this.attributes.acceptedGtc
    },
    signerIdentityEmail(): string | undefined {
      return this.attributes.signerIdentityEmail
    },
    groupVersions(): Set<string> {
      return new Set(this.groupInfo.groupVersions)
    },
    mobile(): string | undefined {
      return this.attributes.mobile
    },
    isFreeUser(): boolean {
      return !this.isBusinessMember && !this.isBusinessAdmin
    },
    highestSignatureQuality(state): string | null {
      if (!state.emailVerified) return null

      if (state.signatureQualities?.qes?.eidas || state.signatureQualities?.qes?.zertes) {
        // If a user did an ident they can only sign with eIDAS. However,
        // it is possible to end up in a ZertES-only case when the user only
        // accepted the ZertES GTC (and not the eIDAS GTC) of Swisscom.
        // Therefore we need to check both keys for 'demo'.
        if (state.signatureQualities.qes.eidas?.quality?.toLowerCase() === 'demo') {
          return 'demo'
        } else if (state.signatureQualities.qes.zertes?.quality?.toLowerCase() === 'demo') {
          return 'demo'
        } else {
          return 'qes'
        }
      } else if (state.signatureQualities?.aes?.any) {
        return 'aes'
      } else if (state.signatureQualities?.ses?.any) {
        return 'ses'
      }

      return null
    },
    part11SignerData(): { name: string; reason: string } | null {
      const name = this.attributes.p11SigningName
      const reason = this.attributes.p11SigningReason

      if (!name || !reason) return null

      return { name, reason }
    },
    preferredLanguage(): Language {
      return this.attributes.lang ?? 'en'
    },
    hasImmutableMobile(): boolean {
      return this.attributes.immutableEidFields?.includes('mobile') || false
    },
    appliedReferralCode(): string | undefined {
      return this.attributes.celloUcc
    },
    role(): AppRole {
      if (!this.id) return 'unauthenticated'

      const directSignStore = useDirectSignStore()

      if (directSignStore.isDirectSign) return 'nas'

      if (this.isBusinessAdmin) return 'admin'
      if (this.isBusinessMember) return 'member'

      return 'user'
    },
    /**
     * Returns the inherited roles of the user.
     */
    accessRoles(): Set<AppRole> {
      const accessRoles = new Set<AppRole>([this.role])

      if (this.role === 'admin') {
        accessRoles.add('member')
        accessRoles.add('user')
      } else if (this.role === 'member') {
        accessRoles.add('user')
      }

      return accessRoles
    },
  },
  actions: {
    async loginUser(email: string, password: string) {
      const authStore = useAuthStore()
      const { userRepository } = useApi()

      const token = await userRepository.login(email, password)
      authStore.storeToken(token, 'Bearer')
    },
    async loginConvertedNasUser(email: string, password: string) {
      const { clearDirectSignSession } = useDirectSignStore()
      await this.loginUser(email, password)
      clearDirectSignSession()
      await this.loadUserBusinessData()
      await this.fetchUser()
    },
    async registerUser(email: string, registerData: RegisterUserPayload) {
      const { userRepository } = useApi()

      await userRepository.register(registerData)

      const authStore = useAuthStore()

      const token = await userRepository.login(email, registerData.password, true)
      authStore.storeToken(token)
    },
    async fetchUser() {
      const directSignStore = useDirectSignStore()
      const languageStore = useLanguageStore()

      const { userRepository, directUserRepository } = useApi()

      const { attributes, ...userData } = directSignStore.isDirectSign
        ? await directUserRepository.get()
        : await userRepository.get()

      let qualities: SignatureQualities | null = null

      try {
        qualities = directSignStore.isDirectSign
          ? await directUserRepository.getSignatureQualities()
          : await userRepository.getSignatureQualities()
      } catch {
        // TODO: Show warning to user
        this.isQualitiesUncertain = true
      }

      const { firstName, lastName, email, emailVerified, ...userAttributes } = attributes

      const userLanguage = userAttributes.lang

      const userIdentity = `${firstName ?? ''} ${lastName ?? ''}`.trim() || email

      if (directSignStore.isDirectSign) {
        const visualSignatureStore = useVisualSignatureStore()

        const defaultSignature = {
          name: userIdentity ?? '',
          language: userLanguage ?? 'en',
          company: '',
          location: '',
          displayClaim: false,
          displayStandards: false,
          displayQr: false,
          displayDate: true,
        }

        visualSignatureStore.$patch({
          signatures: [defaultSignature],
        })
      }

      this.$patch({
        ...userData,
        signatureQualities: qualities ?? {
          ...this.signatureQualities,
          // SES should be available always, so we can use it as a fallback
          ses: { any: { quality: 'SES', legislation: 'ZERTES', provider: 'SKRIBBLE_SES' } },
        },
        firstName: firstName ?? '',
        lastName: lastName ?? '',
        email: email ?? '',
        emailVerified: emailVerified ?? false,
      })

      // We cannot rely on a stable response structure from the backend,
      // so we need to ensure that potentially disappeared attributes
      // are unset (full replacement vs. patch)
      this.attributes = userAttributes

      // Language for NAS signers is set via middleware 05.i18n.global.ts,
      // so we skip changing it here
      if (userLanguage && !directSignStore.isDirectSign) {
        await languageStore.setLanguage(userLanguage)
      }
    },
    // TODO: Harmonise with business store
    async fetchUserBusiness() {
      const { businessRepository } = useApi()

      try {
        return await businessRepository.getDefault()
      } catch {
        return null
      }
    },
    async loadUserBusinessData() {
      const business = await this.fetchUserBusiness()
      if (!business) return
      const stripeSubscriptionStatus = business.billing?.stripeSubscriptionStatus
      if (stripeSubscriptionStatus) {
        Object.assign(business, { stripe: { subscriptionStatus: stripeSubscriptionStatus } })
      }

      const businessStore = useBusinessStore()
      businessStore.$patch(business)
    },
    async setPreferredLanguage(language: Language) {
      if (!this.id) return

      const { userRepository } = useApi()
      const businessStore = useBusinessStore()

      await userRepository.updateAttributes({ lang: language })
      if (businessStore.isStarterPlan) {
        await businessStore.updateChargebeeLanguage(language)
      }
      this.$patch({ attributes: { lang: language } })
    },
    async setNotificationLevel(level: string) {
      if (!this.id) return

      const { userRepository } = useApi()

      await userRepository.updateAttributes({ emailNotificationLevel: level })
      this.$patch({ attributes: { emailNotificationLevel: level } })
    },
    async setDefaultLegislation(legislation: string) {
      if (!this.id) return

      const { userRepository } = useApi()

      await userRepository.updateAttributes({ defaultLegislation: legislation })
      this.$patch({ attributes: { defaultLegislation: legislation } })
    },
    async setDefaultQuality(quality: string) {
      if (!this.id) return

      const { userRepository } = useApi()

      await userRepository.updateAttributes({ defaultQuality: quality })
      this.$patch({ attributes: { defaultQuality: quality } })
    },
    clearUser() {
      const authStore = useAuthStore()
      this.$reset()
      authStore.removeStoredToken()
    },
    async logoutUser(redirectTo = '/login?logged-out') {
      const { $sentry, $auth } = useNuxtApp()
      const { userRepository } = useApi()
      const { resetIdentity } = useAnalytics()

      this.clearUser()
      await userRepository.logout()
      $sentry?.setUser(null)
      resetIdentity()
      $auth.signalLogout()
      window.location.href = redirectTo
    },
    async accept(terms: 'gtc' | 'tou' | 'biz') {
      const { userRepository } = useApi()

      switch (terms) {
        case 'gtc':
          return await userRepository.acceptGeneralTerms()
        case 'tou':
          return await userRepository.acceptSwisscomTerms()
        case 'biz':
          return await userRepository.acceptBusinessInvitation()
      }
    },
  },
})
